import { canCreateCoachUsers } from '@/auth/roles.js';
import { useSession } from '@/auth/useSession.js';
import {
  CoachCsvImportResultView,
  type CoachCsvImportResult,
} from '@/components/coach-csv/coach-csv-import-result-view.js';
import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormInputFile } from '@/components/forms/form-input-file.js';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog.js';
import { Button } from '@/components/ui/button.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { dryRunCoachUsersByCsv, upsertCoachUsersByCsv } from '@/lib/api-calls.js';
import { exportCoachCsvSummaryToCSV } from '@/lib/coach-csv-export.js';
import { csvFormSchema, formatCsvUploadError, TEACHERS_CSV_TEMPLATE_URL } from '@/lib/csv-utils.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { PostCoachUsersCSVFormKeys } from '@airreading/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

interface CreateCoachesByCsvProps {
  trigger: ReactNode;
}

const formSchema = csvFormSchema;

function buildCoachCsvFormData(file: File, previewToken?: string): FormData {
  const formData = new FormData();
  formData.append(PostCoachUsersCSVFormKeys.coachesCSV, file);
  if (previewToken) {
    formData.append('previewToken', previewToken);
  }
  return formData;
}

export const CreateCoachesByCsv = ({ trigger }: CreateCoachesByCsvProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { actingUser } = useSession();
  const pendingImportFileRef = useRef<File | null>(null);
  const pendingImportPreviewTokenRef = useRef<string | null>(null);
  const dryRunFileRef = useRef<File | null>(null);

  const [dryRunResult, setDryRunResult] = useState<CoachCsvImportResult | null>(null);
  const [importResult, setImportResult] = useState<CoachCsvImportResult | null>(null);
  const [confirmPending, setConfirmPending] = useState(false);

  const { mutateAsync: dryRunMutate, isPending: isDryRunPending } = useMutation({
    mutationFn: dryRunCoachUsersByCsv,
  });
  const { mutateAsync: importMutate, isPending: isImportPending } = useMutation({
    mutationFn: upsertCoachUsersByCsv,
  });

  const hasSuccess = dryRunResult?.type === 'success' || importResult?.type === 'success';
  const canImport =
    dryRunResult?.type === 'success' &&
    'previewToken' in dryRunResult.summary &&
    dryRunResult.summary.previewToken !== null &&
    dryRunResult.summary.failures.length === 0 &&
    (dryRunResult.summary.creates.length > 0 || dryRunResult.summary.updates.length > 0);

  // A CSV import can create or update teachers regardless of the page that opened
  // this modal. Invalidate all user- and teacher-related queries so lists, detail
  // pages, typeahead searches, availability views, and derived teacher views do
  // not retain stale data after the import
  const invalidateQueries = () => {
    for (const queryKey of [
      ['USERS'],
      ['USERS_TYPEAHEAD'],
      ['USER'],
      ['ME'],
      ['ME_COACH'],
      ['COACHES'],
      ['COACH'],
      ['COACHES_AVAILABILITIES'],
      ['AVAILABILITY_OVERVIEW'],
      ['COACH_STATES'],
    ]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };

  const onDryRun = async (form: UseFormReturn<z.infer<typeof formSchema>>) => {
    const isValid = await form.trigger('csvFile');
    if (!isValid) {
      return;
    }

    const csvFile = form.getValues('csvFile')[0];
    setDryRunResult(null);
    setImportResult(null);

    try {
      const summary = await dryRunMutate(buildCoachCsvFormData(csvFile));
      dryRunFileRef.current = csvFile;
      setDryRunResult({ type: 'success', summary, isDryRun: true });
    } catch (error: unknown) {
      const { userFacingMessage } = airServerErrorFrom(error) || {};
      setDryRunResult({
        type: 'error',
        message: userFacingMessage ?? 'Dry run failed. Please try again.',
      });
    }
  };

  const onSubmit = ({ formData }: SubmitProps<z.infer<typeof formSchema>>) => {
    if (
      dryRunResult?.type !== 'success' ||
      !('previewToken' in dryRunResult.summary) ||
      !dryRunResult.summary.previewToken ||
      dryRunResult.summary.failures.length > 0 ||
      dryRunFileRef.current !== formData.csvFile[0]
    ) {
      toast({ title: 'Run a dry run before importing this file' });
      return 0;
    }

    pendingImportFileRef.current = formData.csvFile[0];
    pendingImportPreviewTokenRef.current = dryRunResult.summary.previewToken;
    setConfirmPending(true);
    return 0;
  };

  const onConfirmImport = async () => {
    setConfirmPending(false);

    const file = pendingImportFileRef.current;
    const previewToken = pendingImportPreviewTokenRef.current;
    if (!file || !previewToken) {
      toast({ title: 'Select a CSV file before importing' });
      return;
    }

    try {
      const result = await importMutate(buildCoachCsvFormData(file, previewToken));
      setDryRunResult(null);

      if (result.summary) {
        setImportResult({ type: 'success', summary: result.summary, isDryRun: false });
        invalidateQueries();
      } else if (result.errors.length > 0) {
        setImportResult({
          type: 'error',
          message: result.errors.map((error) => formatCsvUploadError(error)).join('\n'),
        });
      }
    } catch (error: unknown) {
      const { userFacingMessage } = airServerErrorFrom(error) || {};
      setImportResult({
        type: 'error',
        message: userFacingMessage ?? 'Import failed. Please try again.',
      });
    }
  };

  const onClose = () => {
    setDryRunResult(null);
    setImportResult(null);
    pendingImportFileRef.current = null;
    pendingImportPreviewTokenRef.current = null;
    dryRunFileRef.current = null;
  };

  const onDownloadSummary = () => {
    const result =
      importResult?.type === 'success' ? importResult : dryRunResult?.type === 'success' ? dryRunResult : null;
    if (!result) {
      return;
    }

    const filename = result.isDryRun ? 'Teachers CSV - Dry Run Summary.csv' : 'Teachers CSV - Import Summary.csv';
    exportCoachCsvSummaryToCSV(result.summary, filename);
  };

  if (!canCreateCoachUsers(actingUser)) {
    return null;
  }

  const confirmTitle = 'Confirm import';
  const confirmDescription = 'This will create and update teachers based on the current dry run preview.';

  return (
    <>
      <AlertDialog open={confirmPending} onOpenChange={(open) => !open && setConfirmPending(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{confirmDescription} Please confirm before proceeding.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void onConfirmImport()}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FormModal
        modalTitle="Import teachers by CSV"
        formSchema={formSchema}
        onSubmit={onSubmit}
        trigger={trigger}
        onClose={onClose}
        confirmBtnLabel="Import"
        isFullScreen={hasSuccess}
        preventExternalClose
        submitButtonProps={{
          loading: isImportPending,
          disabled: isDryRunPending || isImportPending || !canImport,
        }}
        footerContent={({ form }) => (
          <>
            {hasSuccess && (
              <Button
                type="button"
                variant="secondary"
                disabled={isDryRunPending || isImportPending}
                onClick={onDownloadSummary}
              >
                {importResult?.type === 'success' ? 'Download Summary' : 'Download Dry Run Summary'}
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              loading={isDryRunPending}
              disabled={isDryRunPending || isImportPending}
              onClick={() => void onDryRun(form)}
            >
              Dry run
            </Button>
          </>
        )}
      >
        {({ form }) => (
          <div className="grid grid-cols-1 gap-4 py-4">
            <FormInputFile
              form={form}
              name="csvFile"
              label="Teachers CSV"
              inputProps={{
                accept: '.csv',
                className: 'w-full',
                onChange: () => {
                  setDryRunResult(null);
                  setImportResult(null);
                  pendingImportFileRef.current = null;
                  pendingImportPreviewTokenRef.current = null;
                  dryRunFileRef.current = null;
                },
              }}
            />
            <span className="text-sm text-muted-foreground">
              See{' '}
              <a
                href={TEACHERS_CSV_TEMPLATE_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="text-blue-600 hover:underline"
              >
                template
              </a>{' '}
              for CSV format. Export the sheet as a CSV file. Do not change the header row. Omitted columns leave
              existing values unchanged; blank cells are ignored. Use CLEAR in clearable columns to remove a value.
            </span>
            {dryRunResult && <CoachCsvImportResultView result={dryRunResult} />}
            {importResult && <CoachCsvImportResultView result={importResult} />}
          </div>
        )}
      </FormModal>
    </>
  );
};
